// useSelectedLocation.js
import { useState, useEffect } from 'react';

/**
 * Check if a string is a valid MongoDB ObjectId (24 character hex string)
 * @param {string} id - The ID to validate
 * @returns {boolean} True if valid MongoDB ObjectId format
 */
const isValidMongoId = (id) => {
  if (!id || typeof id !== 'string') return false;
  return /^[a-fA-F0-9]{24}$/.test(id);
};

export const useSelectedLocation = () => {
  const [selectedLocation, setSelectedLocation] = useState(() => {
    const storedLocation = localStorage.getItem('selectedLocation') || '';
    // Only return the location if it's a valid MongoDB ObjectId
    // This handles migration from old PostgreSQL integer IDs
    if (storedLocation && !isValidMongoId(storedLocation)) {
      // Clear invalid (old PostgreSQL) ID from localStorage
      localStorage.removeItem('selectedLocation');
      return '';
    }
    return storedLocation;
  });

  useEffect(() => {
    const handleStorageChange = () => {
      const newLocation = localStorage.getItem('selectedLocation') || '';
      // Only update if it's a valid MongoDB ObjectId
      if (newLocation && !isValidMongoId(newLocation)) {
        localStorage.removeItem('selectedLocation');
        setSelectedLocation('');
        return;
      }
      setSelectedLocation(newLocation);
    };

    // Listen for storage events from other tabs/windows
    window.addEventListener('storage', handleStorageChange);

    // Listen for custom storage events from the same tab
    window.addEventListener('storageEvent', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('storageEvent', handleStorageChange);
    };
  }, []);

  return selectedLocation;
};

/**
 * Check if at least one location has been set up
 * @returns {boolean} True if a valid MongoDB location ID exists in localStorage
 */
export const hasLocationSetup = () => {
  const location = localStorage.getItem('selectedLocation');
  // Ensure it's a valid MongoDB ObjectId, not an old PostgreSQL integer
  return location && location !== '' && isValidMongoId(location);
};